import { organizationsId, type OrganizationsId } from "./Organizations";
import { usersId, type UsersId } from "./Users";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for members */
export type MembersId = string & { __flavor: "MembersId" };

/**
 * Represents the table public.members
 * @uniqueConstraints {"members_pkey":{"name":"members_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"members_user_id_index":{"name":"members_user_id_index","isPrimary":false,"isUnique":false,"columns":[{"name":"user_id","definition":"user_id","predicate":null}]},"members_organization_id_index":{"name":"members_organization_id_index","isPrimary":false,"isUnique":false,"columns":[{"name":"organization_id","definition":"organization_id","predicate":null}]}}
 */
export default interface MembersTable {
  id: ColumnType<MembersId, MembersId, MembersId>;

  organizationId: ColumnType<OrganizationsId, OrganizationsId, OrganizationsId>;

  userId: ColumnType<UsersId, UsersId, UsersId>;

  role: ColumnType<string, string | undefined, string>;

  createdAt: ColumnType<Date, Date | string, Date | string>;
}

export type Members = Selectable<MembersTable>;

export type NewMembers = Insertable<MembersTable>;

export type MembersUpdate = Updateable<MembersTable>;

export const membersId = z.string() as unknown as z.Schema<MembersId>;

export const members = z.object({
  id: membersId,
  organizationId: organizationsId,
  userId: usersId,
  role: z.string(),
  createdAt: z.date(),
});

export const membersInitializer = z.object({
  id: membersId,
  organizationId: organizationsId,
  userId: usersId,
  role: z.string().optional(),
  createdAt: z.date(),
});

export const membersMutator = z.object({
  id: membersId.optional(),
  organizationId: organizationsId.optional(),
  userId: usersId.optional(),
  role: z.string().optional(),
  createdAt: z.date().optional(),
});

// ── constraints for Members ─────────────────────────────────
export const MembersConstraints = {
  members_pkey: {
    name: "members_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  members_user_id_index: {
    name: "members_user_id_index",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "user_id", definition: "user_id", predicate: null }],
  },
  members_organization_id_index: {
    name: "members_organization_id_index",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "organization_id", definition: "organization_id", predicate: null }],
  },
} as const;
export type MembersConstraintsType = typeof MembersConstraints;
